import type { Plugin } from 'chart.js';
import type { ChartTheme } from './chart-theme';
import { formatNumber } from './format';

/**
 * Escribe el VALOR sobre cada barra y cada punto de línea.
 *
 * ── Reglas que sigue (guía de visualización) ───────────────────────────────
 * · El texto lleva el token de TINTA, nunca el color de la serie: la identidad
 *   la carga la marca de color, el número solo informa la magnitud.
 * · Un halo del color de la tarjeta detrás del texto lo hace legible sobre
 *   cualquier relleno, sin recuadros.
 * · Un punto NULO no se rotula: no hay cifra que escribir.
 * · En barras verticales muy angostas (series agrupadas) se OMITE la etiqueta en
 *   lugar de encimarla: un número ilegible informa menos que su ausencia.
 *
 * No se usa una dependencia externa: el dibujo es directo sobre el lienzo, en
 * `afterDatasetsDraw`, para quedar por encima de las marcas.
 */
export function dataLabelsPlugin(theme: ChartTheme, valueFormat: 'integer' | 'percent'): Plugin {
  return {
    id: 'oxxeanDataLabels',
    afterDatasetsDraw(chart) {
      const { ctx } = chart;
      const type = (chart.config as { type?: string }).type;
      if (type === 'doughnut' || type === 'pie') return;

      const horizontal = chart.options.indexAxis === 'y';
      const font = (getComputedStyle(document.documentElement).getPropertyValue('--font') || 'system-ui').trim();

      ctx.save();
      ctx.font = `600 11px ${font}`;
      ctx.textBaseline = 'middle';

      chart.data.datasets.forEach((dataset, di) => {
        const meta = chart.getDatasetMeta(di);
        if (meta.hidden) return;
        const isBar = meta.type === 'bar';

        meta.data.forEach((element, i) => {
          const raw = dataset.data[i];
          if (typeof raw !== 'number') return;

          const point = element.getProps(['x', 'y', 'width', 'height', 'base'], true) as {
            x: number;
            y: number;
            width?: number;
            height?: number;
            base?: number;
          };

          // Solo se omite una barra de ancho de un pelo (sub-6px): en las series
          // AGRUPADAS —dotación/ingresos/egresos— cada barra ronda los 14 px y debe
          // llevar su número igual. Las alturas distintas (dotación arriba, los
          // flujos abajo) evitan que las etiquetas del mismo mes se encimen.
          if (isBar && !horizontal && (point.width ?? 0) < 6) return;

          const text = formatNumber(raw, valueFormat);
          let lx = point.x;
          let ly = point.y;

          if (isBar && horizontal) {
            // A la derecha del extremo de la barra.
            lx = point.x + 6;
            ly = point.y;
            ctx.textAlign = 'left';
          } else {
            // Sobre la barra vertical o el punto de la línea.
            lx = point.x;
            ly = point.y - 9;
            ctx.textAlign = 'center';
            // Si el tope queda pegado al borde superior, el número baja adentro.
            if (ly < 12) ly = point.y + 12;
          }

          ctx.lineWidth = 3;
          ctx.strokeStyle = theme.surface;
          ctx.strokeText(text, lx, ly);
          ctx.fillStyle = theme.text;
          ctx.fillText(text, lx, ly);
        });
      });

      ctx.restore();
    },
  };
}
